// src/auth/Signup.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import './Login.css';

const API_URL = import.meta.env.VITE_API_URL;

// ===== バリデーションスキーマ =====
const signupSchema = z
  .object({
    userName: z
      .string()
      .min(1, '氏名を入力してください')
      .max(100, '氏名は100文字以内で入力してください'),
    userAddress: z
      .string()
      .min(1, 'メールアドレスを入力してください')
      .email('正しいメールアドレス形式で入力してください'),
    userPhone: z
      .string()
      .min(1, '電話番号を入力してください')
      .regex(/^[\d\-+() ]{7,15}$/, '電話番号の形式が正しくありません'),
    userPass: z
      .string()
      .min(8, 'パスワードは8文字以上にしてください'),
    userPass2: z
      .string()
      .min(1, 'パスワード（確認）を入力してください'),
  })
  .refine((data) => data.userPass === data.userPass2, {
    message: 'パスワードが一致しません',
    path: ['userPass2'],
  });

// ===== コンポーネント =====
const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isPasswordVisible1, setIsPasswordVisible1] = useState(false);
  const [isPasswordVisible2, setIsPasswordVisible2] = useState(false);
  const [serverError, setServerError] = useState('');

  // ===== React Hook Form =====
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      userName: '',
      userAddress: '',
      userPhone: '',
      userPass: '',
      userPass2: '',
    },
  });

  // ---- OTP成功で戻ってきたら登録を続行 ----
  useEffect(() => {
    const st = location.state;
    if (st?.otpVerified && st?.email) {
      const draftRaw = sessionStorage.getItem('signupDraft');
      if (!draftRaw) {
        setServerError('入力内容が失われました。もう一度やり直してください。');
        return;
      }
      try {
        const draft = JSON.parse(draftRaw);
        if ((draft.userAddress || '').trim().toLowerCase() !== st.email.trim().toLowerCase()) {
          setServerError('メールアドレスが一致しません。もう一度やり直してください。');
          return;
        }
        void finalizeSignup(draft);
      } catch {
        setServerError('入力内容の復元に失敗しました。もう一度やり直してください。');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ---- OTP成功後の最終登録処理 ----
  const finalizeSignup = async (draft) => {
    try {
      setServerError('');

      const res = await axios.post(`${API_URL}/api/auth/signup/create`, {
        userName: draft.userName,
        userAddress: draft.userAddress,
        userPhone: draft.userPhone,
        userPass: draft.userPass,
      });

      if (res.data?.success) {
        sessionStorage.removeItem('signupDraft');
        sessionStorage.removeItem('otpFlowState');
        localStorage.setItem('lastLoginID', draft.userAddress);
        navigate('/auth/notice/signup-thanks');
      } else {
        setServerError(res.data?.message || '登録に失敗しました。');
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  // ---- API エラーハンドリング ----
  const handleApiError = (err) => {
    const status = err?.response?.status;
    const msg =
      err?.response?.data?.message ??
      err?.response?.data?.error ??
      '通信エラーが発生しました。';

    if (status === 409) {
      setServerError('このユーザーIDは既に登録されています。');
    } else if (status === 429) {
      setServerError('操作が集中しています。しばらくしてからお試しください。');
    } else if (status === 400) {
      setServerError(msg || '入力内容を確認してください。');
    } else if (status === 403) {
      setServerError('CSRFエラーが発生しました。ページを再読み込みしてやり直してください。');
    } else {
      setServerError(msg);
    }
  };

  // ---- フォーム送信 ----
  const onSubmit = async (data) => {
    try {
      setServerError('');
      const email = data.userAddress.trim();

      // ① 事前に登録済みチェック
      try {
        const chk = await axios.post(`${API_URL}/api/auth/signup/check`, {
          userAddress: email,
        });
        if (!chk.data?.available) {
          setServerError('このユーザーIDは既に登録されています。');
          return;
        }
      } catch (chkErr) {
        if (chkErr?.response?.status === 409) {
          setServerError('このユーザーIDは既に登録されています。');
          return;
        }
        const msg =
          chkErr?.response?.data?.message ??
          chkErr?.response?.data?.error ??
          '登録可否の確認に失敗しました。時間をおいて再度お試しください。';
        setServerError(msg);
        return;
      }

      // ② 未登録なら OTP 開始
      const otpRes = await axios.post(`${API_URL}/api/otp/start`, {
        userAddress: email,
        purpose: 'signup',
      });

      const { challengeId, ttlSec, resendCooldownSec, maskedEmail } = otpRes.data || {};
      if (!challengeId) {
        setServerError('ワンタイムパスの開始に失敗しました。時間をおいて再度お試しください。');
        return;
      }

      // 入力一時退避
      sessionStorage.setItem(
        'signupDraft',
        JSON.stringify({
          userName: data.userName.trim(),
          userAddress: email,
          userPhone: data.userPhone.trim(),
          userPass: data.userPass,
          userPass2: data.userPass2,
        })
      );

      sessionStorage.setItem(
        'otpFlowState',
        JSON.stringify({
          challengeId,
          userAddress: email,
          maskedEmail,
          ttlSec,
          resendCooldownSec,
          returnTo: '/signup',
          returnState: { otpVerified: true, email },
        })
      );

      // ③ OTP画面へ
      navigate('/otpverify', {
        state: {
          challengeId,
          userAddress: email,
          maskedEmail,
          ttlSec,
          resendCooldownSec,
          returnTo: '/signup',
          returnState: { otpVerified: true, email },
        },
        replace: true,
      });
    } catch (err) {
      handleApiError(err);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="signup-title">
        <h2 id="signup-title" className="login-title">新規登録</h2>

        {serverError && <div className="error-alert" role="alert">{serverError}</div>}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* 氏名 */}
          <div className="form-group">
            <label htmlFor="userName">氏名</label>
            <input
              id="userName"
              type="text"
              placeholder="山田 太郎"
              aria-invalid={!!errors.userName}
              {...register('userName')}
            />
            {errors.userName && (
              <span className="field-error">{errors.userName.message}</span>
            )}
          </div>

          {/* メールアドレス */}
          <div className="form-group">
            <label htmlFor="userAddress">ユーザーID（メールアドレス）</label>
            <input
              id="userAddress"
              type="email"
              placeholder="your@example.com"
              autoComplete="username"
              aria-invalid={!!errors.userAddress}
              {...register('userAddress')}
            />
            {errors.userAddress && (
              <span className="field-error">{errors.userAddress.message}</span>
            )}
          </div>

          {/* 電話番号 */}
          <div className="form-group">
            <label htmlFor="userPhone">電話番号</label>
            <input
              id="userPhone"
              type="tel"
              placeholder="090-0000-0000"
              autoComplete="tel"
              aria-invalid={!!errors.userPhone}
              {...register('userPhone')}
            />
            {errors.userPhone && (
              <span className="field-error">{errors.userPhone.message}</span>
            )}
          </div>

          {/* パスワード */}
          <div className="form-group">
            <label htmlFor="userPass">パスワード</label>
            <div className="password-field">
              <input
                id="userPass"
                type={isPasswordVisible1 ? 'text' : 'password'}
                placeholder="8文字以上"
                autoComplete="new-password"
                aria-invalid={!!errors.userPass}
                {...register('userPass')}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setIsPasswordVisible1((v) => !v)}
                aria-label={isPasswordVisible1 ? 'パスワードを非表示' : 'パスワードを表示'}
              >
                {isPasswordVisible1 ? '隠す' : '表示'}
              </button>
            </div>
            {errors.userPass && (
              <span className="field-error">{errors.userPass.message}</span>
            )}
          </div>

          {/* パスワード確認 */}
          <div className="form-group">
            <label htmlFor="userPass2">パスワード（確認）</label>
            <div className="password-field">
              <input
                id="userPass2"
                type={isPasswordVisible2 ? 'text' : 'password'}
                placeholder="もう一度入力"
                autoComplete="new-password"
                aria-invalid={!!errors.userPass2}
                {...register('userPass2')}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setIsPasswordVisible2((v) => !v)}
                aria-label={isPasswordVisible2 ? 'パスワードを非表示' : 'パスワードを表示'}
              >
                {isPasswordVisible2 ? '隠す' : '表示'}
              </button>
            </div>
            {errors.userPass2 && (
              <span className="field-error">{errors.userPass2.message}</span>
            )}
          </div>

          <button
            type="submit"
            className={`login-button ${isSubmitting ? 'is-loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? '登録中…' : '登録する'}
          </button>
        </form>

        <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13 }}>
          すでにアカウントをお持ちの方は <Link to="/login">ログイン</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;